import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight, GraduationCap, ChevronRight, Sparkles, AlertOctagon, CalendarClock } from "lucide-react";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { BRL, CHART_COLORS, ScopeToggle } from "./shared";

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const fmtMonth = (ym) => {
  const m = parseInt((ym || "").slice(5, 7), 10);
  return m ? MESES[m - 1] : ym;
};

export default function Overview({ scope, setScope }) {
  const [s, setS] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get(`/transactions/summary?scope=${scope}`).then(({ data }) => setS(data));
    api.get(`/transactions?scope=${scope}`).then(({ data }) => setRecent(data.slice(0, 5)));
  }, [scope]);

  const monthly = (s?.monthly || []).map((m) => ({ mes: fmtMonth(m.month), Receitas: m.income, Despesas: m.expense }));
  const totalCat = (s?.by_category || []).reduce((a, c) => a + c.value, 0) || 1;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" data-testid="overview-section">
      {/* Main column */}
      <div className="xl:col-span-2 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="uppercase text-[11px] tracking-[0.2em] text-text-muted font-body">Painel</p>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-text-primary">Visão Geral</h2>
          </div>
          <ScopeToggle scope={scope} setScope={setScope} />
        </div>

        {/* Hero card */}
        <div className="hero-navy rounded-3xl p-6 sm:p-8 text-white shadow-xl shadow-primary/20 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/5" />
          <div className="absolute right-10 bottom-0 h-40 w-40 rounded-full bg-secondary/20 blur-2xl" />
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-white/60 font-body text-sm">Saldo do período</p>
              <p className="font-heading text-4xl font-extrabold tracking-tight mt-1">{BRL(s?.balance)}</p>
            </div>
            <span className="text-xs font-body bg-white/10 rounded-full px-3 py-1.5 border border-white/15">
              {scope === "business" ? "Empresarial" : "Pessoal"}
            </span>
          </div>

          <div className="relative h-40 mt-4 -mx-2">
            {monthly.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthly} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ffffff" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E7A9BC" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#E7A9BC" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="mes" stroke="rgba(255,255,255,0.5)" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => BRL(v)}
                    contentStyle={{ background: "#002A57", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, color: "#fff" }} />
                  <Area type="monotone" dataKey="Receitas" stroke="#ffffff" strokeWidth={2.5} fill="url(#gInc)" />
                  <Area type="monotone" dataKey="Despesas" stroke="#E7A9BC" strokeWidth={2.5} fill="url(#gExp)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-white/50 font-body text-sm">
                Sem dados ainda — adicione transações ou importe um extrato.
              </div>
            )}
          </div>

          <div className="relative grid grid-cols-3 gap-3 mt-2">
            {[
              { label: "Receitas", val: s?.income, Icon: ArrowUpRight },
              { label: "Despesas", val: s?.expense, Icon: ArrowDownRight },
              { label: "Transações", val: null, count: s?.count ?? 0, Icon: Wallet },
            ].map((x) => (
              <div key={x.label} className="glass rounded-2xl p-4 border border-white/20 !bg-white/10">
                <x.Icon className="h-4 w-4 text-white/70" />
                <p className="text-[11px] text-white/60 font-body mt-2">{x.label}</p>
                <p className="font-heading text-lg font-bold tracking-tight">
                  {x.val != null ? BRL(x.val) : x.count}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Gargalo de dinheiro */}
        {(s?.top_category || s?.deficit_months?.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="bottleneck-section">
            {s?.top_category && (
              <div className="bg-secondary/5 border border-secondary/20 rounded-2xl p-5" data-testid="bottleneck-category">
                <div className="flex items-center gap-2 text-secondary">
                  <AlertOctagon className="h-4 w-4" />
                  <p className="uppercase text-[11px] tracking-[0.18em] font-body font-semibold">Gargalo de dinheiro</p>
                </div>
                <p className="font-heading text-xl font-bold tracking-tight text-text-primary mt-2">{s.top_category.category}</p>
                <p className="text-text-secondary font-body text-sm mt-1">
                  {BRL(s.top_category.value)} · {s.top_category.pct}% de todas as despesas
                </p>
              </div>
            )}
            <div className="bg-surface border border-border rounded-2xl p-5" data-testid="bottleneck-deficit">
              <div className="flex items-center gap-2 text-text-secondary">
                <CalendarClock className="h-4 w-4" />
                <p className="uppercase text-[11px] tracking-[0.18em] font-body font-semibold">Meses no negativo</p>
              </div>
              {s?.deficit_months?.length ? (
                <div className="mt-2 space-y-1">
                  {s.deficit_months.slice(-3).map((d) => (
                    <p key={d.month} className="text-sm font-body text-text-primary">
                      {fmtMonth(d.month)}: <span className="text-secondary font-semibold">-{BRL(d.deficit)}</span>
                    </p>
                  ))}
                </div>
              ) : (
                <p className="font-heading text-xl font-bold tracking-tight text-primary mt-2">Nenhum mês no negativo</p>
              )}
            </div>
          </div>
        )}

        {/* Category progress cards */}
        <div>
          <h3 className="font-heading text-lg font-bold tracking-tight text-text-primary mb-4">Despesas por categoria</h3>
          {s?.by_category?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {s.by_category.slice(0, 6).map((c, i) => {
                const pct = Math.round((c.value / totalCat) * 100);
                return (
                  <div key={c.category} className="bg-surface border border-border rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between">
                      <span className="font-body font-medium text-text-primary">{c.category}</span>
                      <span className="font-heading font-bold text-text-primary">{BRL(c.value)}</span>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    </div>
                    <p className="text-xs text-text-muted font-body mt-2">{pct}% das despesas</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-surface border border-border rounded-2xl p-8 text-center text-text-muted font-body">
              Nenhuma despesa registrada ainda.
            </div>
          )}
        </div>
      </div>

      {/* Right panel */}
      <div className="space-y-6">
        <div className="card-rose rounded-3xl p-6 text-white shadow-xl shadow-secondary/20 relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 h-40 w-40 rounded-full bg-white/10" />
          <GraduationCap className="h-8 w-8" />
          <h3 className="font-heading text-xl font-bold tracking-tight mt-4">Aprenda com os bestsellers</h3>
          <p className="text-white/80 font-body text-sm mt-2 leading-relaxed">
            Dicas de Pai Rico Pai Pobre, Psicologia Financeira e mais — personalizadas com IA.
          </p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-body font-medium bg-white/15 rounded-full px-4 py-2">
            <Sparkles className="h-4 w-4" /> Veja em Educação
          </div>
        </div>

        <div className="bg-surface border border-border rounded-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold tracking-tight text-text-primary">Transações recentes</h3>
          </div>
          <div className="space-y-3 max-h-[380px] overflow-y-auto scrollbar-thin pr-1" data-testid="recent-tx">
            {recent.length === 0 && <p className="text-sm text-text-muted font-body py-6 text-center">Nenhuma transação.</p>}
            {recent.map((t) => (
              <div key={t.id} className="flex items-center gap-3">
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${
                  t.type === "income" ? "bg-primary/10 text-primary" : "bg-secondary/10 text-secondary"}`}>
                  {t.type === "income" ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-body text-sm font-medium text-text-primary truncate">{t.description || t.category}</p>
                  <p className="text-xs text-text-muted font-body">{t.category} · {t.date}</p>
                </div>
                <p className={`font-body text-sm font-semibold ${t.type === "income" ? "text-primary" : "text-secondary"}`}>
                  {t.type === "income" ? "+" : "-"}{BRL(t.amount)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
